import React, { useMemo, useState } from 'react';
import { addDays, addMonths, addWeeks, eachDayOfInterval, endOfMonth, endOfWeek, format, parseISO, startOfMonth, startOfWeek, subMonths, subWeeks } from 'date-fns';
import { nl } from 'date-fns/locale';
import { BanIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';
import { useStudio } from '../../contexts/StudioContext';
import { Booking } from '../../types/booking';
import { ACTIVE_STATUSES, utilisationForDate } from '../../utils/availability';
import { dayStats } from '../../utils/agenda';
import { euro, longDate, monthLabel } from '../../utils/format';
import { AgendaMonth } from '../../components/admin/AgendaMonth';
import { AgendaWeek } from '../../components/admin/AgendaWeek';
import { AgendaList } from '../../components/admin/AgendaList';
import { AgendaDay } from '../../components/admin/AgendaDay';
import { BookingDetail } from '../../components/admin/BookingDetail';
import { NewBookingDialog } from '../../components/admin/NewBookingDialog';
type View = 'month' | 'week' | 'day';
const views: {
  id: View;
  label: string;
}[] = [{
  id: 'month',
  label: 'Maand'
}, {
  id: 'week',
  label: 'Week'
}, {
  id: 'day',
  label: 'Dag'
}];
export function Agenda() {
  const {
    bookings,
    services,
    settings,
    toggleBlockedDate
  } = useStudio();
  const [view, setView] = useState<View>('month');
  const [selected, setSelected] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [open, setOpen] = useState<Booking | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const cursor = parseISO(selected);
  const blocked = settings.blockedDates.includes(selected);
  const heading = useMemo(() => {
    if (view === 'month') return monthLabel(cursor);
    if (view === 'day') return longDate(selected);
    const from = startOfWeek(cursor, {
      weekStartsOn: 1
    });
    const to = endOfWeek(cursor, {
      weekStartsOn: 1
    });
    return `${format(from, 'd MMM', {
      locale: nl
    })} — ${format(to, 'd MMM', {
      locale: nl
    })}`;
  }, [view, cursor, selected]);
  const range = useMemo(() => {
    if (view === 'day') return [selected];
    const from = view === 'week' ? startOfWeek(cursor, {
      weekStartsOn: 1
    }) : startOfMonth(cursor);
    const to = view === 'week' ? endOfWeek(cursor, {
      weekStartsOn: 1
    }) : endOfMonth(cursor);
    return eachDayOfInterval({
      start: from,
      end: to
    }).map(d => format(d, 'yyyy-MM-dd'));
  }, [view, cursor, selected]);
  const totals = useMemo(() => {
    const rows = bookings.filter(b => range.includes(b.date) && ACTIVE_STATUSES.includes(b.status));
    return {
      count: rows.length,
      revenue: rows.reduce((sum, b) => sum + b.amount, 0),
      pending: rows.filter(b => b.status === 'pending').length,
      hours: Math.round(rows.reduce((sum, b) => sum + b.durationMinutes, 0) / 60 * 10) / 10
    };
  }, [bookings, range]);
  const step = (direction: 1 | -1) => {
    if (view === 'month') {
      setSelected(format(direction === 1 ? addMonths(cursor, 1) : subMonths(cursor, 1), 'yyyy-MM-dd'));
      return;
    }
    if (view === 'week') {
      setSelected(format(direction === 1 ? addWeeks(cursor, 1) : subWeeks(cursor, 1), 'yyyy-MM-dd'));
      return;
    }
    setSelected(format(addDays(cursor, direction), 'yyyy-MM-dd'));
  };
  const stats = dayStats(bookings, selected);
  return <div className="w-full max-w-full px-4 py-5 lg:px-8 lg:py-8" data-id="element-759" data-mp="3u68h2h3iwbw2">
      <div className="flex rounded-2xl border border-edge bg-carbon p-1" data-id="element-760" data-mp="3faaot9wpzrp4">
        {views.map(item => <button key={item.id} type="button" onClick={() => setView(item.id)} aria-pressed={view === item.id} className={`flex-1 rounded-xl py-2.5 text-sm transition-colors duration-200 ease-smooth ${view === item.id ? 'bg-ember font-semibold text-void' : 'text-dim'}`} data-id="element-761" data-mp="3kbqb15amw493">
            {item.label}
          </button>)}
      </div>

      <div className="mt-3 flex items-center gap-2" data-id="element-762" data-mp="19frflir7a2p8">
        <button type="button" onClick={() => step(-1)} aria-label="Vorige periode" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-panel text-chalk active:text-ember" data-id="element-763" data-mp="35jxlzdyg5pm9">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-764" data-mp="2rlhg1p8es356" />
        </button>
        <button type="button" onClick={() => setSelected(format(new Date(), 'yyyy-MM-dd'))} className="min-w-0 flex-1 truncate rounded-2xl bg-panel px-4 py-3 text-center font-display text-sm font-extrabold capitalize tracking-tight text-chalk" data-id="element-765" data-mp="2rnk2jzacre55">
          {heading}
        </button>
        <button type="button" onClick={() => step(1)} aria-label="Volgende periode" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-panel text-chalk active:text-ember" data-id="element-766" data-mp="27xi0m12wg7tf">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-767" data-mp="1yy4unidzi1dg" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-768" data-mp="edzng2lx833g">
        <div className="rounded-2xl border border-edge bg-carbon px-4 py-3" data-id="element-769" data-mp="jdcivbquyihn">
          <span className="block text-xs text-dim" data-id="element-770" data-mp="nffxg98tq375">Afspraken</span>
          <span className="mt-1 block font-display text-xl font-extrabold text-chalk" data-id="element-771" data-mp="ng8vmltpvkdo">{totals.count}</span>
        </div>
        <div className="rounded-2xl border border-edge bg-carbon px-4 py-3" data-id="element-772" data-mp="sc6bys4zcqqg">
          <span className="block text-xs text-dim" data-id="element-773" data-mp="3rvihq4uv06ca">Omzet</span>
          <span className="mt-1 block font-display text-xl font-extrabold text-chalk" data-id="element-774" data-mp="3vuhlhm4utx25">
            {euro(totals.revenue)}
          </span>
        </div>
        <div className="hidden rounded-2xl border border-edge bg-carbon px-4 py-3 lg:block" data-id="element-775" data-mp="4n500uwxuxgf">
          <span className="block text-xs text-dim" data-id="element-776" data-mp="9hrej6ybh64q">Werkuren</span>
          <span className="mt-1 block font-display text-xl font-extrabold text-chalk" data-id="element-777" data-mp="384wxupxrabmj">{totals.hours} u</span>
        </div>
        <div className="hidden rounded-2xl border border-edge bg-carbon px-4 py-3 lg:block" data-id="element-778" data-mp="3d2yivgeqsl0n">
          <span className="block text-xs text-dim" data-id="element-779" data-mp="k8gegd9uqw7c">Open aanvragen</span>
          <span className="mt-1 block font-display text-xl font-extrabold text-chalk" data-id="element-780" data-mp="fa55s5vamrqp">{totals.pending}</span>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2" data-id="element-781" data-mp="t1tiobdd9k8q">
        <button type="button" onClick={() => setCreating(true)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-ember px-4 py-3 text-sm font-semibold text-void active:bg-white" data-id="element-782" data-mp="p0rctboy29xs">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-783" data-mp="3sjt39gdui435" />
          Nieuwe afspraak
        </button>
        <button type="button" onClick={() => toggleBlockedDate(selected)} aria-label={blocked ? 'Dag weer openen' : 'Dag blokkeren'} className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${blocked ? 'border-ember bg-ember text-void' : 'border-edge bg-carbon text-dim'}`} data-id="element-784" data-mp="aa0mk8303iws">
          <BanIcon className="h-5 w-5" aria-hidden="true" data-id="element-785" data-mp="1p7mrchmbtlbt" />
        </button>
      </div>

      <p className="mt-3 rounded-2xl border border-edge bg-carbon px-4 py-3 text-xs text-dim" data-id="element-786" data-mp="1tbkqutfkcy5e">
        <span className="font-medium capitalize text-chalk" data-id="element-787" data-mp="1fc1s2r8n6z5r">{longDate(selected)}</span> · {stats.count} afspraken
        · {euro(stats.revenue)} · bezetting {utilisationForDate(bookings, selected, settings)}%
        {blocked && <span className="text-ember" data-id="element-788" data-mp="280h7hevorjhm"> · geblokkeerd</span>}
      </p>

      <div className="mt-4" data-id="element-789" data-mp="2cxqt0jfyfb06">
        {view === 'month' && <>
            <AgendaMonth month={cursor} bookings={bookings} settings={settings} selected={selected} onSelectDate={setSelected} onOpenBooking={setOpen} data-id="element-790" data-mp="1yae54o2oct6m" />
            <div className="mt-4 sm:hidden" data-id="element-791" data-mp="2qx8n9ec0rggx">
              <AgendaList anchor={cursor} days={1} bookings={bookings} settings={settings} services={services} onSelectDate={iso => {
            setSelected(iso);
            setView('day');
          }} onOpenBooking={setOpen} data-id="element-792" data-mp="2vp06f3suijx1" />
            </div>
          </>}

        {view === 'week' && <>
            <div className="hidden lg:block" data-id="element-793" data-mp="hxgk1z1adtb0">
              <AgendaWeek anchor={cursor} bookings={bookings} settings={settings} selected={selected} onSelectDate={setSelected} onOpenBooking={setOpen} data-id="element-794" data-mp="1emehxq4c27ce" />
            </div>
            <div className="lg:hidden" data-id="element-795" data-mp="10mdimf3pv7z1">
              <AgendaList anchor={cursor} bookings={bookings} settings={settings} services={services} onSelectDate={iso => {
            setSelected(iso);
            setView('day');
          }} onOpenBooking={setOpen} data-id="element-796" data-mp="10lbtj6pv8kyb" />
            </div>
          </>}

        {view === 'day' && <AgendaDay iso={selected} bookings={bookings} settings={settings} services={services} onOpenBooking={setOpen} data-id="element-797" data-mp="25tmti5tc84v9" />}
      </div>

      <p className="mt-4 hidden flex-wrap items-center gap-5 text-xs text-dim lg:flex" data-id="element-798" data-mp="2ent4h4vt9ies">
        <span className="flex items-center gap-2" data-id="element-799" data-mp="2jkjkxtbj2g0q">
          <span className="h-2.5 w-2.5 rounded-sm border border-ember/55 bg-ember/15" aria-hidden="true" data-id="element-800" data-mp="2oj36hsm7lpsk" />
          Bevestigd
        </span>
        <span className="flex items-center gap-2" data-id="element-801" data-mp="2tjqus6j1zsj3">
          <span className="h-2.5 w-2.5 rounded-sm border border-amber-400/50 bg-amber-400/10" aria-hidden="true" data-id="element-802" data-mp="2xl2mqipegpug" />
          Aanvraag
        </span>
        <span className="flex items-center gap-2" data-id="element-803" data-mp="136jiobp7meab">
          <span className="h-2.5 w-2.5 rounded-sm border border-emerald-400/45 bg-emerald-400/10" aria-hidden="true" data-id="element-804" data-mp="1371qm5l8igy2" />
          Afgerond
        </span>
        <span className="flex items-center gap-2" data-id="element-805" data-mp="3hre7av8y5op3">
          <span className="h-2.5 w-2.5 rounded-sm bg-void/60 ring-1 ring-edge" aria-hidden="true" data-id="element-806" data-mp="3mpfmv675z5vt" />
          Gesloten
        </span>
      </p>

      {open && <BookingDetail booking={open} onClose={() => setOpen(null)} data-id="element-807" data-mp="3rpbpp244kr9s" />}

      {creating && <NewBookingDialog date={selected} onClose={() => setCreating(false)} onCreated={ref => {
      setToast(`Afspraak ${ref} ingepland.`);
      window.setTimeout(() => setToast(null), 4000);
    }} data-id="element-808" data-mp="h6781n1ygj4" />}

      {toast && <p role="status" className="fixed bottom-28 left-1/2 z-[70] -translate-x-1/2 rounded-full border border-ember/50 bg-carbon px-5 py-3 text-sm text-chalk lg:bottom-8" data-id="element-809" data-mp="e9w08q100lpq">
          {toast}
        </p>}
    </div>;
}